import { useState } from "react";

import { getCurrentUser } from "@modules/Authentication/services/currentUser";

import {
  buildTrackingParams,
  TRACKING_EVENTS,
  trackFunnelEvent,
} from "../config/tracking";
import { claimServiceRequestDraft } from "../services/serviceRequestCreation";
import { getDraftToken } from "./useDraftStorage";

/**
 * Claim the draft for the session the auth step just created — the visitor's
 * end of the flow. A logged-in user publishes from the last config screen
 * instead (`DynamicScreen`), unless the draft predates their login and has no
 * owner yet, which needs this claim as well. Both then hand their state to
 * `TaskCreationOutcomeModals`, so the visitor lands on the same request page,
 * success modal on top.
 *
 * The draft was already screened by that last config screen, which is what
 * `claim` requires — it publishes, it does not save. Rejects rather than
 * opening the modal on failure, so the caller can say why.
 */
export const usePublishServiceRequest = (screenKey: string) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedRequestId, setPublishedRequestId] = useState<string | null>(
    null
  );

  const publish = async () => {
    const publicToken = getDraftToken();
    if (!publicToken) throw new Error("No service request draft to publish");

    setIsPublishing(true);

    try {
      // Read before `claim` can accept this very task and make it count.
      const currentUser = await getCurrentUser({}).then(
        (response) => response.data,
        () => undefined
      );
      const serviceRequest = await claimServiceRequestDraft(publicToken);

      // Guarded on the id: the success modal's CTA has nowhere to go without
      // one, and `/service_requests/undefined` is worse than staying put.
      if (serviceRequest?.id) setPublishedRequestId(String(serviceRequest.id));

      trackFunnelEvent(
        TRACKING_EVENTS.TASK_PUBLISHED,
        buildTrackingParams({
          draft: serviceRequest,
          screenKey,
          isLoggedIn: true,
        })
      );

      if (!currentUser?.hasTasks)
        trackFunnelEvent(TRACKING_EVENTS.FIRST_SERVICE_REQUEST_CREATED);
    } finally {
      setIsPublishing(false);
    }
  };

  return { publish, isPublishing, publishedRequestId };
};
